import { lazy, FC, useState, Suspense } from "react";
import { isNullOrWhitespace } from "@app/core/utilities/string";
import { AppNotLoaded } from "@app/components/pages/AppNotLoaded";
import { LoadingPage } from "@app/components/common/LoadingPage";
import { PortalConfiguration, SiteConfigurationProvider } from "@dit/ui-configurations/configurations";
import { PortalConfigurationOptions } from "@dit/ui-configurations/configurations/models";
import { createDefaultConfig, GlobalConfigDefinition } from "@app/core/globalConfig";
import { AppInsightsLoader, reactPlugin } from "./core/insights/azure/AppInsight";
import { AppInsightsContext } from "@microsoft/applicationinsights-react-js";
import { I18nConfiguration } from "./I18nConfiguration";

const Root = lazy(() => import(/* webpackChunkName: "root" */ "@app/Root"));

export const AsyncRoot: FC<{ baseConfig: GlobalConfigDefinition; configurationProvider: SiteConfigurationProvider }> = ({
    baseConfig,
    configurationProvider,
}) => {
    const [isLoading, setIsLoading] = useState(configurationProvider !== SiteConfigurationProvider.local);
    const [hasError, setHasError] = useState(false);
    const [errorStatusCode, setErrorStatusCode] = useState("");
    const combinedConfig = createDefaultConfig({ ...baseConfig });

    const handleOnError = (e: any) => {
        setHasError(true);
        setErrorStatusCode(e.message);
        setIsLoading(false);
    };

    const handleOnSuccess = (config: GlobalConfigDefinition) => {
        // update favicon
        const linkFavicon = document.getElementById("favicon") as HTMLLinkElement;
        linkFavicon.href = !isNullOrWhitespace(config.faviconUrl) ? config.faviconUrl : "favicon.png";

        setIsLoading(false);
    };

    const options: PortalConfigurationOptions<GlobalConfigDefinition> = {
        configurationProvider: configurationProvider,
        remoteConfigurationUrl: new URL(`${combinedConfig.services.ConfigurationAPI.uri}/SiteConfigurationValues`),
        onFetchFailure: handleOnError,
        onFetchSuccess: handleOnSuccess,
    };

    return hasError ? (
        <AppNotLoaded errorStatusCode={errorStatusCode} />
    ) : (
        <PortalConfiguration options={options} defaultConfiguration={combinedConfig}>
            <AppInsightsLoader>
                <AppInsightsContext.Provider value={reactPlugin}>
                    <I18nConfiguration>
                        {isLoading ? (
                            <LoadingPage />
                        ) : (
                            <Suspense fallback={<LoadingPage />}>
                                <Root />
                            </Suspense>
                        )}
                    </I18nConfiguration>
                </AppInsightsContext.Provider>
            </AppInsightsLoader>
        </PortalConfiguration>
    );
};
